import { DotsThreeVertical } from '@phosphor-icons/react';
import ApiErrorParser from 'api/ApiErrorParser';
import classNames from 'classnames';
import { useAccount } from 'context/AccountContext';
import { isFuture, isSameDay, startOfDay } from 'date-fns';
import { usePlanning } from 'hooks/UsePlanning';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DropdownMenu from 'ui/DropdownMenu';
import { Spinner } from 'ui/Loading';
import { SpinnerSize } from 'ui/Loading/Spinner';
import { CalendarView, GroupBy } from 'utilities/Planning';

export interface Props {
  day: Date;

  // Highlight the day. Useful when it's today.
  highlight?: boolean;
  className?: string;

  // Width of the item
  columnHeaderWidth?: number;

  calendarView: CalendarView;
}

/**
 * Small component that displays a day. This can be used in the day and multi day calendars.
 *   +--------+
 *   |   DO   |
 *   |    7   |
 *   +--------+
 *
 */
export function DayGridHeader({ day, highlight, className, columnHeaderWidth, calendarView }: Props): JSX.Element {
  const { formatDateIntl } = useAccount();
  const { markActivityAsDone, setModalError, groupBy } = usePlanning();
  const [busy, setBusy] = useState<boolean>(false);
  const { t } = useTranslation();

  // Mark all activities as done.
  const markAllActivitiesAsDone = useCallback(async () => {
    if (isFuture(startOfDay(day))) {
      setModalError(t('cant-set-future-activities-to-done', 'Cannot mark activities in the future to done.'));
      return;
    }
    function delay(ms: number) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
    setBusy(true);
    let errors: string[] = [];
    for (const cluster of calendarView.current.clusters) {
      for await (const act of cluster.columns.find(col => isSameDay(col.day, day))?.activities ?? []) {
        if (!act.done) {
          try {
            await markActivityAsDone(act, true);
          } catch (e) {
            const err = new ApiErrorParser(e);
            errors = errors.concat(err.nonFieldErrorsStrings());
          }
          // Workaround for the backend. We need a 250ms delay between marking
          // the activities as done. Otherwise a deadlock appears in the backend.
          // See https://gitlab.equinem.com/equinem/equinemcore/-/issues/878 for
          // details.
          await delay(250);
        }
      }
    }
    if (errors.length > 0) {
      setModalError(errors.join('\n\n'));
    }
    setBusy(false);
  }, [calendarView, day, markActivityAsDone, setModalError, setBusy, t]);

  /**
   * Define the actions for the dropdown
   */
  const actions = useMemo(() => {
    if ([GroupBy.Horse, GroupBy.Stable, GroupBy.Staff, GroupBy.StaffCatchAll].includes(groupBy)) {
      return [
        [
          {
            element: t('mark-all-activities-as-done', 'Mark all activities as done'),
            onClick: markAllActivitiesAsDone,
          },
        ],
      ];
    } else {
      return [];
    }
  }, [t, markAllActivitiesAsDone, groupBy]);

  return (
    <div
      style={{ width: columnHeaderWidth }}
      className={classNames(
        'select-none flex flex-row md:flex-col justify-center items-center text-white/80 md:text-black/50 gap-1 relative',
        className,
      )}
    >
      <p className={classNames('text-xs uppercase mt-0.5', { 'text-white md:text-blue-500': highlight })}>
        {formatDateIntl(day, { weekday: 'short' })}
      </p>
      <div
        className={classNames('rounded-full w-6 h-6 md:text-2xl md:w-10 md:h-10 flex items-center justify-center', {
          'bg-white/90 text-primary md:text-white md:bg-blue-500': highlight,
        })}
      >
        <p>{day.getDate()}</p>
      </div>
      {actions.length > 0 && (
        <div className='absolute w-5 flex items-center justify-center right-0 top-0'>
          <DropdownMenu menuPlacement='bottom end' menuItems={actions}>
            <button>
              {busy && <Spinner size={SpinnerSize.XSmall} />}
              {!busy && <DotsThreeVertical size={20} weight='bold' />}
            </button>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
